import {
  cloneElement,
  type CSSProperties,
  isValidElement,
  type KeyboardEvent,
  type MouseEvent,
  ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styles from './Dialog.module.css';

// Length of the enter / exit animations, plus a small buffer.
const ANIMATION_MS = 250;

type DialogProps = {
  children: ReactNode;
  className?: string;
  maxWidth?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
};

type DialogCloseProps = {
  // when "asChild" is true, children should be a button
  children: ReactNode;
  className?: string;
  asChild?: boolean;
};

type ChildWithOnclick = {
  onClick?: (e: MouseEvent) => void;
};

function isChildWithOnclick(child: ReactNode): child is ReactElement<ChildWithOnclick> {
  return isValidElement(child) && 'onClick' in child.props;
}

function Dialog({
  children,
  className = '',
  maxWidth = 400,
  open,
  onOpenChange,
  title,
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  // Holds whichever animation is in flight; opening and closing never overlap.
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isContentMounted, setIsContentMounted] = useState(open);

  // Children stay unmounted while the dialog is closed so flows start on their
  // first screen and nothing keeps running in the background. They mount in the
  // same commit that opens the dialog, and unmount once it has animated out.
  if (open && !isContentMounted) {
    setIsContentMounted(true);
  }

  const style = useMemo(
    () =>
      ({
        '--dialog-max-width': maxWidth ? `${maxWidth / 16}rem` : '21.875rem',
      }) as CSSProperties,
    [maxWidth],
  );

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Reopening mid-exit has to re-enter an already open dialog, otherwise it
    // stays open wearing .exiting, which is invisible.
    if (open && (!dialog.open || dialog.classList.contains(styles.exiting))) {
      if (!dialog.open) {
        dialog.show();
      }

      // Marks the window in which the dialog itself is in motion, so content can
      // opt out of its own transitions until the dialog has come to rest.
      dialog.dataset.animating = 'true';
      animationTimeoutRef.current = setTimeout(() => {
        delete dialog.dataset.animating;
        animationTimeoutRef.current = null;
      }, ANIMATION_MS);

      // Delay adding .entered to allow browser to paint initial state
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dialog.classList.remove(styles.exiting);
          dialog.classList.add(styles.entered);
        });
      });
    } else if (!open && dialog.open) {
      dialog.dataset.animating = 'true';
      dialog.classList.remove(styles.entered);
      dialog.classList.add(styles.exiting);

      animationTimeoutRef.current = setTimeout(() => {
        if (dialog.open) {
          dialog.close();
        }
        delete dialog.dataset.animating;
        animationTimeoutRef.current = null;
        setIsContentMounted(false);
      }, ANIMATION_MS);
    }

    // Cancels the timeout for whichever animation this run interrupted.
    // eslint-disable-next-line consistent-return
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, [open]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDialogElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onOpenChange(false);
      }
    },
    [onOpenChange],
  );

  const handleClose = useCallback(() => {
    const dialog = dialogRef.current;

    if (!dialog) return;

    const wasExiting = dialog.classList.contains(styles.exiting);

    if (wasExiting) {
      // Clean up .exiting class
      dialog.classList.remove(styles.exiting);
    } else {
      // Only trigger onOpenChange if we're NOT already closing
      // If we were .exiting, onOpenChange was already called
      onOpenChange(false);
    }
  }, [onOpenChange]);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={dialogRef}
      className={`${styles.dialog} ${className}`}
      onClose={handleClose}
      onKeyDown={handleKeyDown}
      aria-label={title}
      style={style}
    >
      <div className={styles['dialog-inner']}>{isContentMounted && children}</div>
    </dialog>
  );
}

function DialogClose({ children, className, asChild }: DialogCloseProps) {
  const handleClick = useCallback((e: MouseEvent) => {
    const dialog = e.currentTarget.closest<HTMLDialogElement>('dialog');
    if (dialog) {
      const closeEvent = new Event('close');
      dialog.dispatchEvent(closeEvent);
    }
  }, []);

  // If asChild, clone the child and add our onClick
  if (asChild && isValidElement(children)) {
    return cloneElement(children, {
      onClick: (e: MouseEvent) => {
        handleClick(e);
        if (isChildWithOnclick(children)) {
          children.props.onClick?.(e);
        }
      },
    });
  }

  return (
    <button type="button" onClick={handleClick} className={className}>
      {children}
    </button>
  );
}

export { Dialog, DialogClose, type DialogCloseProps, type DialogProps };
